import React, { useState, useEffect } from 'react';
import {
    Card, Select, Button, Table, Modal, DatePicker, message,
    Row, Col, Statistic, Tag, Progress, Space, InputNumber, Input
} from 'antd';
import { CalculatorOutlined, TrophyOutlined, StarFilled } from '@ant-design/icons';
import QoCService from '../../services/QoCService';
import { usePeriods, periodToForm } from '../../hooks/usePeriods';
import FacilityService from '../../services/FacilityService';
import { AntdSelect, AntdDatepicker } from '../../utils/Antd';
import moment from 'moment';
import { trailCreate, trailPageView, TRAIL_MODULES } from '../../utils/activityTrail';
const RATINGS = [
    { min: 98,  max: 100,  label: 'Outstanding',           stars: 5, color: '#52c41a', bg: '#f6ffed' },
    { min: 95,  max: 97.9, label: 'Excellent',             stars: 4, color: '#1890ff', bg: '#e6f4ff' },
    { min: 90,  max: 94.9, label: 'Good',                  stars: 3, color: '#faad14', bg: '#fffbe6' },
    { min: 85,  max: 89.9, label: 'Fair',                  stars: 2, color: '#fa8c16', bg: '#fff7e6' },
    { min: 70,  max: 84.9, label: 'Requires Improvement',  stars: 1, color: '#f5222d', bg: '#fff2f0' },
    { min: 0,   max: 69.9, label: 'Inadequate',            stars: 0, color: '#820014', bg: '#ffebe8' },
];

const getRating = (score) =>
    RATINGS.find(r => score >= r.min && score <= r.max) || RATINGS[RATINGS.length - 1];

const Stars = ({ count }) => (
    <span>
        {Array.from({ length: 5 }, (_, i) => (
            <StarFilled key={i} style={{ color: i < count ? '#faad14' : '#d9d9d9', fontSize: 14 }} />
        ))}
    </span>
);

export default function QoCQualityIndex() {
    const { periods, currentPeriod } = usePeriods();
    const [qualityData, setQualityData] = useState([]);
    const [facilities, setFacilities]   = useState([]);
    const [loading, setLoading]         = useState(false);
    const [calcModal, setCalcModal]     = useState(false);
    const [calcForm, setCalcForm]       = useState({});
    const [filters, setFilters]         = useState({});

    useEffect(() => {
        trailPageView(TRAIL_MODULES.QUALITY_INDEX, window.location.pathname);
        loadFacilities();
    }, []);

    // Auto-load when currentPeriod is available
    useEffect(() => {
        if (currentPeriod) {
            const f = { period_label: currentPeriod.label };
            setFilters(f);
            loadQualityIndex(f);
        }
    }, [currentPeriod?.label]);

    const loadFacilities = async () => {
        try {
            const res = await FacilityService.all({ status: 1 });
            setFacilities(res.data.result.data.map(v => ({
                id: v.id, name: v.business_name, region: v.region, site_type: v.site_type,
            })));
        } catch {}
    };

    const loadQualityIndex = async (f = filters) => {
        setLoading(true);
        try {
            const res = await QoCService.getQualityIndex(f);
            setQualityData(res.data?.data || []);
        } catch { message.error('Failed to load quality index'); }
        finally { setLoading(false); }
    };

    const handleCalculate = async () => {
        if (!calcForm.facility_id || !calcForm.period_label ||
            !calcForm.period_start || !calcForm.period_end) {
            message.error('All fields are required');
            return;
        }
        setLoading(true);
        try {
            await QoCService.calculateQualityIndex({
                ...calcForm,
                period_start: moment(calcForm.period_start).format('YYYY-MM-DD'),
                period_end:   moment(calcForm.period_end).format('YYYY-MM-DD'),
            });

            const facilityName = facilities.find(f => f.id === calcForm.facility_id)?.name || String(calcForm.facility_id);
            trailCreate(TRAIL_MODULES.QUALITY_INDEX, 'Quality Index', calcForm.facility_id, {
                facilityName,
                period: calcForm.period_label,
            });
            message.success('Quality index calculated');
            setCalcModal(false);
            loadQualityIndex();
        } catch { message.error('Failed to calculate'); }
        finally { setLoading(false); }
    };

    const avgQI = qualityData.length
        ? (qualityData.reduce((s, r) => s + parseFloat(r.quality_index || 0), 0) / qualityData.length).toFixed(1)
        : 0;
    const topPerformers  = qualityData.filter(r => parseFloat(r.quality_index) >= 90).length;
    const needsAttention = qualityData.filter(r => parseFloat(r.quality_index) < 70).length;

    const columns = [
        {
            title: 'Facility', dataIndex: 'facility_name', width: 200,
            render: (n, r) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{n}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>
                        {r.region} · {r.site_type}
                    </div>
                </div>
            ),
        },
        {
            title: 'Period', dataIndex: 'period_label', width: 110,
            render: p => p ? <Tag color="blue">{p}</Tag> : <span style={{ color: '#ccc' }}>—</span>,
        },
        {
            title: 'QoC Score', dataIndex: 'qoc_score', width: 160,
            render: (s, r) => {
                const w = parseFloat(r.qoc_weight || 80);
                const score = parseFloat(s || 0);
                return (
                    <div>
                        <span style={{ fontWeight: 700, fontSize: 15 }}>{score.toFixed(1)}%</span>
                        <div style={{ fontSize: 11, color: '#888' }}>
                            × {w}% = {(score * w / 100).toFixed(1)} pts
                        </div>
                    </div>
                );
            },
        },
        {
            // FIX: API returns survey_composite (not survey_score)
            // inpatient_score, outpatient_score, inpatient_count, outpatient_count also available
            title: 'Survey Score', width: 210,
            render: (_, r) => {
                const composite  = parseFloat(r.survey_composite  || 0);
                const w          = parseFloat(r.survey_weight      || 20);
                const ipScore    = parseFloat(r.inpatient_score    || 0);
                const opScore    = parseFloat(r.outpatient_score   || 0);
                const ipW        = parseFloat(r.inpatient_weight   || 50);
                const opW        = parseFloat(r.outpatient_weight  || 50);
                const ipCount    = parseInt(r.inpatient_count      || 0);
                const opCount    = parseInt(r.outpatient_count     || 0);
                const totalCount = ipCount + opCount;

                return (
                    <div>
                        <span style={{
                            fontWeight: 700, fontSize: 15,
                            color: totalCount === 0 ? '#f5222d' : undefined,
                        }}>
                            {composite.toFixed(1)}%
                        </span>
                        {totalCount === 0 && (
                            <Tag color="orange" style={{ marginLeft: 6, fontSize: 10 }}>No surveys</Tag>
                        )}
                        <div style={{ fontSize: 11, color: '#888' }}>
                            × {w}% = {(composite * w / 100).toFixed(1)} pts
                        </div>
                        <div style={{ fontSize: 10, color: '#aaa' }}>
                            IP({ipW}%): {ipScore.toFixed(0)} · OP({opW}%): {opScore.toFixed(0)}
                            &nbsp;·&nbsp;{totalCount} survey{totalCount !== 1 ? 's' : ''}
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'Quality Index', dataIndex: 'quality_index', width: 150,
            sorter: (a, b) => parseFloat(a.quality_index) - parseFloat(b.quality_index),
            defaultSortOrder: 'descend',
            render: score => {
                const s = parseFloat(score || 0);
                const rating = getRating(s);
                return (
                    <div style={{
                        backgroundColor: rating.bg, borderRadius: 6,
                        padding: '6px 10px', border: `1px solid ${rating.color}22`,
                    }}>
                        <div style={{ fontWeight: 800, fontSize: 18, color: rating.color }}>
                            {s.toFixed(1)}%
                        </div>
                        <Progress percent={s} showInfo={false} size="small" strokeColor={rating.color} />
                    </div>
                );
            },
        },
        {
            title: 'Rating', width: 180,
            render: (_, r) => {
                const rating = getRating(parseFloat(r.quality_index || 0));
                return (
                    <div>
                        <Stars count={rating.stars} />
                        <div style={{ marginTop: 4 }}>
                            <Tag color={rating.color}
                                style={{ border: 'none', color: '#fff', backgroundColor: rating.color }}>
                                {rating.label}
                            </Tag>
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'Calculated', dataIndex: 'calculated_at', width: 120,
            render: d => d ? moment(d).format('DD MMM YYYY') : '—',
        },
    ];

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto"><h2>Quality Index</h2></div>
            </div>
            <div className="page-pad">

                {/* Formula explanation */}
                <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f0f5ff', borderColor: '#adc6ff' }}>
                    <Row gutter={16} align="middle">
                        <Col span={2} style={{ textAlign: 'center' }}>
                            <CalculatorOutlined style={{ fontSize: 32, color: '#1890ff' }} />
                        </Col>
                        <Col span={22}>
                            <div style={{ fontWeight: 700, fontSize: 15, color: '#1890ff', marginBottom: 4 }}>
                                Quality Index = (QoC Assessment Score × 80%) + (Customer Satisfaction Survey Score × 20%)
                            </div>
                            <div style={{ color: '#555', fontSize: 13 }}>
                                The QoC score comes from the approved assessment overall percentage.
                                The survey score is the average of all customer satisfaction surveys captured
                                for the facility in the selected period. Rated using the standard 6-level star rating scale.
                            </div>
                        </Col>
                    </Row>
                </Card>

                {/* Summary */}
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={6}>
                        <Card size="small" style={{ borderTop: '3px solid #1890ff', textAlign: 'center' }}>
                            <Statistic title="Total Facilities" value={qualityData.length} valueStyle={{ color: '#1890ff' }} />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card size="small" style={{ borderTop: '3px solid #faad14', textAlign: 'center' }}>
                            <Statistic title="Avg Quality Index" value={avgQI} suffix="%" valueStyle={{ color: '#faad14' }} />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card size="small" style={{ borderTop: '3px solid #52c41a', textAlign: 'center' }}>
                            <Statistic title="Top Performers (≥90%)" value={topPerformers} valueStyle={{ color: '#52c41a' }} />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card size="small" style={{ borderTop: '3px solid #f5222d', textAlign: 'center' }}>
                            <Statistic title="Needs Attention (<70%)" value={needsAttention} valueStyle={{ color: '#f5222d' }} />
                        </Card>
                    </Col>
                </Row>

                <Card>
                    <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div style={{ flex: 1, minWidth: 160 }}>
                            <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Facility</label>
                            <AntdSelect allowClear placeholder="All" options={facilities}
                                value={filters.facility_id}
                                onChange={v => setFilters({ ...filters, facility_id: v })}
                                style={{ width: '100%' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 140 }}>
                            <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Period</label>
                            <Select allowClear placeholder="All Periods" style={{ width: '100%' }}
                                value={filters.period_label || undefined}
                                onChange={v => {
                        const f = { ...filters, period_label: v || '' };
                        setFilters(f);
                        loadQualityIndex(f);
                    }}>
                                {periods.map(p => (
                                    <Select.Option key={p.label} value={p.label}>
                                        {parseInt(p.is_current) === 1 ? '★ ' : ''}{p.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                        <div style={{ flex: 1, minWidth: 140 }}>
                            <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Region</label>
                            <Input placeholder="Region" value={filters.region}
                                onChange={e => setFilters({ ...filters, region: e.target.value })} />
                        </div>
                        <Button onClick={() => loadQualityIndex()}>Search</Button>
                        <Button type="primary" icon={<CalculatorOutlined />}
                            onClick={() => { setCalcForm({}); setCalcModal(true); }}>
                            Calculate Quality Index
                        </Button>
                    </div>
                    <Table columns={columns} dataSource={qualityData} rowKey="facility_id"
                        loading={loading}
                        pagination={{ pageSize: 20, showTotal: t => `${t} facilities` }} />
                </Card>
            </div>

            <Modal title="Calculate Quality Index" open={calcModal}
                onCancel={() => setCalcModal(false)} onOk={handleCalculate}
                confirmLoading={loading} okText="Calculate" width={500}>
                <Row gutter={16}>
                    <Col span={24} style={{ marginBottom: 12 }}>
                        <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Facility *</label>
                        <AntdSelect showSearch options={facilities} value={calcForm.facility_id}
                            onChange={v => setCalcForm({ ...calcForm, facility_id: v })}
                            style={{ width: '100%' }} />
                    </Col>
                    <Col span={24} style={{ marginBottom: 12 }}>
                        <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Period *</label>
                        <Select style={{ width: '100%' }}
                            value={calcForm.period_label || undefined}
                            onChange={v => {
                                const p = periods.find(x => x.label === v);
                                if (p) setCalcForm(f => ({ ...f, ...periodToForm(p) }));
                            }}>
                            {periods.map(p => (
                                <Select.Option key={p.label} value={p.label}>
                                    {parseInt(p.is_current) === 1 ? '★ ' : ''}{p.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                    {calcForm.period_start && (
                        <Col span={24} style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: 12, color: '#888', padding: '6px 10px',
                                background: '#f5f5f5', borderRadius: 6 }}>
                                Period: {moment(calcForm.period_start).format('DD MMM YYYY')}
                                {' — '}
                                {moment(calcForm.period_end).format('DD MMM YYYY')}
                            </div>
                        </Col>
                    )}
                </Row>
                <div style={{ marginTop: 12, padding: 12, backgroundColor: '#f0f5ff',
                    borderRadius: 6, fontSize: 13, color: '#555' }}>
                    This will find the latest approved assessment and all survey responses within the
                    selected period, then compute the quality index automatically.
                </div>
            </Modal>
        </div>
    );
}